import {
    getLastFourAccountNumber,
    getSpendAccountCategory,
} from '@fruitful/fruitils';
import type {
    AccountSummary,
    HolisticSummary,
    HolisticSummaryCategory,
} from '@fruitful/types';
import {
    AccountCategory,
    Provider,
    SpendAccountCategory,
} from '@fruitful/types';

import type { MembershipDetails } from '../../../entities/entityTypes';

const HOLISTIC_CATEGORY_ORDER: HolisticSummaryCategory[] = [
    AccountCategory.SPEND,
    AccountCategory.SAVE,
    AccountCategory.INVEST,
    AccountCategory.BORROW,
];

export const flattenHolisticAccounts = (
    summary: HolisticSummary | undefined,
): AccountSummary[] => {
    if (summary == null) {
        return [];
    }
    const list: AccountSummary[] = [];
    for (const cat of HOLISTIC_CATEGORY_ORDER) {
        const section = summary[cat];
        list.push(...(section?.accounts ?? []));
    }
    return list.sort((a, b) => {
        const an = `${a.institutionName ?? ''} ${a.name ?? ''}`.toLowerCase();
        const bn = `${b.institutionName ?? ''} ${b.name ?? ''}`.toLowerCase();
        return an.localeCompare(bn);
    });
};

/** Borrow and SPEND+credit (cards/lines) are liabilities — omit from current assets. */
export const shouldShowInMoneyMapperCurrentAssets = (
    account: AccountSummary,
): boolean => {
    if (account.category === AccountCategory.BORROW) {
        return false;
    }
    if (account.category === AccountCategory.SPEND) {
        const spendKind =
            account.spendAccountCategory ??
            getSpendAccountCategory(account.type ?? account.rawType);
        if (spendKind === SpendAccountCategory.CREDIT) {
            return false;
        }
    }
    return true;
};

/** Display as `*1234` when last four exist. */
export const formatAccountLastFourDisplay = (
    accountNumberMasked: string | undefined,
): string => {
    if (accountNumberMasked == null || accountNumberMasked.trim() === '') {
        return '—';
    }
    const lastFour = getLastFourAccountNumber(accountNumberMasked);
    return lastFour === '' ? '—' : `*${lastFour}`;
};

export const getOwnershipShortLabel = (
    membership: MembershipDetails,
    account: AccountSummary,
): string => {
    if (account.provider === Provider.SYNCTERA) {
        if (account.jointOwnerUserId != null) {
            return 'Joint';
        }
        const m = membership.users.find(
            (u) => u.id === account.primaryOwnerUserId,
        );
        return m?.firstName ?? '—';
    }
    if (account.userId == null) {
        return '—';
    }
    const m = membership.users.find((u) => u.id === account.userId);
    return m?.firstName ?? '—';
};
